import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const OpenSourceContributions = () => {
  const [activeTab, setActiveTab] = useState('contributions');

  const contributions = [
    {
      id: 1,
      repo: "facebook/react",
      title: "Fix memory leak in useEffect cleanup",
      type: "Bug Fix",
      status: "Merged",
      date: "2024-01-15",
      description: "Resolved memory leak issue in useEffect cleanup function that was causing performance degradation in large applications.",
      impact: "Improved performance for 2M+ developers",
      languages: ["JavaScript", "TypeScript"],
      stars: 218000,
      url: "https://github.com/facebook/react/pull/12345"
    },
    {
      id: 2,
      repo: "vercel/next.js",
      title: "Add support for custom webpack loaders",
      type: "Feature",
      status: "Merged",
      date: "2023-12-08",
      description: "Added configuration option to support custom webpack loaders in Next.js build process.",
      impact: "Enhanced build flexibility",
      languages: ["JavaScript", "TypeScript"],
      stars: 118000,
      url: "https://github.com/vercel/next.js/pull/67890"
    },
    {
      id: 3,
      repo: "tailwindlabs/tailwindcss",
      title: "Improve dark mode color contrast",
      type: "Enhancement",
      status: "Under Review",
      date: "2024-01-20",
      description: "Enhanced color contrast ratios in dark mode to meet WCAG accessibility standards.",
      impact: "Better accessibility compliance",
      languages: ["CSS", "JavaScript"],
      stars: 78000,
      url: "https://github.com/tailwindlabs/tailwindcss/pull/11111"
    }
  ];

  const ownProjects = [
    {
      id: 1,
      name: "react-performance-monitor",
      description: "A lightweight React component for monitoring and visualizing component render performance in development.",
      stars: 1247,
      forks: 89,
      language: "TypeScript",
      lastUpdate: "2024-01-18",
      status: "Active",
      downloads: "15K/month",
      features: ["Real-time monitoring", "Performance metrics", "Visual charts", "Zero production overhead"]
    },
    {
      id: 2,
      name: "css-grid-generator",
      description: "Interactive CSS Grid generator with visual editor and code export functionality.",
      stars: 892,
      forks: 156,
      language: "JavaScript",
      lastUpdate: "2023-12-22",
      status: "Maintained",
      downloads: "8K/month",
      features: ["Visual editor", "Code export", "Responsive design", "Template library"]
    },
    {
      id: 3,
      name: "api-mock-server",
      description: "Fast and flexible mock API server for frontend development with realistic data generation.",
      stars: 634,
      forks: 78,
      language: "Node.js",
      lastUpdate: "2024-01-10",
      status: "Active",
      downloads: "12K/month",
      features: ["RESTful APIs", "GraphQL support", "Data relationships", "Custom schemas"]
    }
  ];

  const stats = {
    totalContributions: 247,
    repositoriesContributed: 23,
    pullRequestsMerged: 89,
    issuesResolved: 156,
    starsEarned: 2773,
    forksReceived: 323
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Merged': return 'bg-success text-white';
      case 'Under Review': return 'bg-yellow-500 text-white';
      case 'Active': return 'bg-green-500 text-white';
      case 'Maintained': return 'bg-blue-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Bug Fix': return 'Bug';
      case 'Feature': return 'Plus';
      case 'Enhancement': return 'Sparkles';
      default: return 'Code';
    }
  };

  return (
    <div className="bg-white rounded-2xl p-8 shadow-soft border border-border">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-text-primary mb-2">Open Source Contributions</h2>
        <p className="text-text-secondary">
          Contributing to the developer community through open source projects and collaborative development.
        </p>
      </div>
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {Object.entries(stats)?.map(([key, value], index) => (
          <div key={index} className="bg-muted rounded-xl p-4 text-center">
            <div className="text-2xl font-bold text-accent">{value?.toLocaleString()}</div>
            <div className="text-xs text-text-secondary capitalize">
              {key?.replace(/([A-Z])/g, ' $1')?.trim()}
            </div>
          </div>
        ))}
      </div>
      {/* Tab Navigation */}
      <div className="flex space-x-1 mb-6 bg-muted rounded-lg p-1">
        <button
          onClick={() => setActiveTab('contributions')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-300 ${
            activeTab === 'contributions' ?'bg-white text-accent shadow-soft' :'text-text-secondary hover:text-text-primary'
          }`}
        >
          Contributions
        </button>
        <button
          onClick={() => setActiveTab('projects')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all duration-300 ${
            activeTab === 'projects' ?'bg-white text-accent shadow-soft' :'text-text-secondary hover:text-text-primary'
          }`}
        >
          My Projects
        </button>
      </div>
      {/* Contributions Tab */}
      {activeTab === 'contributions' && (
        <div className="space-y-6">
          {contributions?.map((contribution) => (
            <div key={contribution?.id} className="bg-muted rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-accent rounded-lg flex items-center justify-center">
                    <Icon name={getTypeIcon(contribution?.type)} size={20} color="white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-1">{contribution?.title}</h3>
                    <div className="flex items-center space-x-2 text-sm text-text-secondary mb-2">
                      <Icon name="Github" size={14} />
                      <span>{contribution?.repo}</span>
                      <span>•</span>
                      <span>{contribution?.date}</span>
                    </div>
                    <p className="text-sm text-text-secondary">{contribution?.description}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end space-y-2">
                  <span className={`text-xs px-3 py-1 rounded-full ${getStatusColor(contribution?.status)}`}>
                    {contribution?.status}
                  </span>
                  <div className="flex items-center space-x-1 text-xs text-text-secondary">
                    <Icon name="Star" size={12} />
                    <span>{contribution?.stars?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="flex items-center space-x-1 text-sm text-success">
                    <Icon name="TrendingUp" size={14} />
                    <span>{contribution?.impact}</span>
                  </div>
                  <div className="flex space-x-2">
                    {contribution?.languages?.map((lang, index) => (
                      <span key={index} className="text-xs bg-white px-2 py-1 rounded-full text-text-secondary">
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="ExternalLink"
                  iconPosition="right"
                  className="text-accent border-accent hover:bg-accent hover:text-white"
                >
                  View PR
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Projects Tab */}
      {activeTab === 'projects' && (
        <div className="space-y-6">
          {ownProjects?.map((project) => (
            <div key={project?.id} className="bg-muted rounded-xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-text-primary mb-2">{project?.name}</h3>
                  <p className="text-text-secondary mb-3">{project?.description}</p>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {project?.features?.map((feature, index) => (
                      <span key={index} className="text-xs bg-accent/10 text-accent px-2 py-1 rounded-full">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                <span className={`text-xs px-3 py-1 rounded-full ${getStatusColor(project?.status)}`}>
                  {project?.status}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-1 text-yellow-500 mb-1">
                    <Icon name="Star" size={16} />
                    <span className="font-semibold">{project?.stars}</span>
                  </div>
                  <div className="text-xs text-text-secondary">Stars</div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-1 text-blue-500 mb-1">
                    <Icon name="GitFork" size={16} />
                    <span className="font-semibold">{project?.forks}</span>
                  </div>
                  <div className="text-xs text-text-secondary">Forks</div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-1 text-green-500 mb-1">
                    <Icon name="Download" size={16} />
                    <span className="font-semibold">{project?.downloads}</span>
                  </div>
                  <div className="text-xs text-text-secondary">Downloads</div>
                </div>
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-1 text-purple-500 mb-1">
                    <Icon name="Code" size={16} />
                    <span className="font-semibold">{project?.language}</span>
                  </div>
                  <div className="text-xs text-text-secondary">Language</div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="text-sm text-text-secondary">
                  Last updated: {project?.lastUpdate}
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Github"
                    iconPosition="left"
                    className="text-text-secondary hover:text-text-primary"
                  >
                    Source
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="ExternalLink"
                    iconPosition="right"
                    className="text-accent border-accent hover:bg-accent hover:text-white"
                  >
                    Demo
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Call to Action */}
      <div className="mt-8 pt-6 border-t border-border">
        <div className="text-center">
          <h3 className="font-semibold text-text-primary mb-2">Interested in collaborating?</h3>
          <p className="text-text-secondary mb-4">
            I'm always looking for interesting open source projects to contribute to.
          </p>
          <div className="flex justify-center space-x-3">
            <Button
              variant="default"
              iconName="Github"
              iconPosition="left"
              className="bg-accent hover:bg-hover text-white"
            >
              Follow on GitHub
            </Button>
            <Button
              variant="outline"
              iconName="Mail"
              iconPosition="left"
              className="text-accent border-accent hover:bg-accent hover:text-white"
            >
              Suggest Project
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenSourceContributions;